<template>
  <el-dialog
      v-model="dialogVisible"
      title=""
      width="420px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      class="bind-phone-dialog"
  >
    <template #header>
      <div class="dialog-custom-header">
        <span class="dialog-title">绑定手机</span>
      </div>
    </template>

    <el-form :model="bindForm" ref="bindFormRef" @submit.prevent="handleBind">
      <el-form-item prop="phone">
        <el-input
            v-model="bindForm.phone"
            placeholder="请输入手机号"
            size="large"
            clearable
            maxlength="11"
            @input="clearPhoneError"
            @blur="validatePhoneOnBlur"
            @keyup.enter="handleBind"
        >
          <template #prefix>
            <el-icon><Iphone /></el-icon>
          </template>
        </el-input>
        <div v-if="phoneError" class="error-message" role="alert">
          <el-icon><WarningFilled /></el-icon>
          <span>{{ phoneError }}</span>
        </div>
      </el-form-item>

      <el-form-item prop="captcha">
        <el-input
            v-model="bindForm.captcha"
            placeholder="请输入验证码"
            size="large"
            maxlength="6"
            @input="clearCaptchaError"
            @keyup.enter="handleBind"
        >
          <template #prefix>
            <el-icon><Message /></el-icon>
          </template>
          <template #suffix>
            <el-button
                link
                class="captcha-btn"
                :disabled="captchaCooldown > 0 || isSendingCaptcha"
                @click="sendCaptcha"
            >
              {{ isSendingCaptcha ? '发送中...' : (captchaCooldown > 0 ? `${captchaCooldown}秒后重试` : '获取验证码') }}
            </el-button>
          </template>
        </el-input>
        <div v-if="captchaError" class="error-message" role="alert">
          <el-icon><WarningFilled /></el-icon>
          <span>{{ captchaError }}</span>
        </div>
        <div v-if="captchaSuccess" class="success-message" role="status">
          <el-icon><SuccessFilled /></el-icon>
          <span>{{ captchaSuccess }}</span>
        </div>
      </el-form-item>
    </el-form>

    <!-- 微信绑定提示 -->
    <div class="wechat-bind-tip">
      <p>绑定成功后，系统将自动为您创建账号</p>
    </div>

    <template #footer>
      <el-button
          type="primary"
          @click="handleBind"
          :loading="loading"
          :disabled="!isFormValid"
          style="width: 100%"
      >
        绑定手机
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, watch, computed, onBeforeUnmount, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { Iphone, Message, WarningFilled, SuccessFilled } from '@element-plus/icons-vue'
import request from '@/utils/request'
import { useAuthState } from '@/stores/authState'
import { validateCaptcha } from '@/utils/authValidators'

const { setToken } = useAuthState()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  sessionId: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['update:modelValue', 'bind-success'])

// ---- 响应式数据 ----
const dialogVisible = ref(false)
const bindForm = reactive({
  phone: '',
  captcha: ''
})
const captchaCooldown = ref(0)
const isSendingCaptcha = ref(false)
const loading = ref(false)
const phoneError = ref('')
const captchaError = ref('')
const captchaSuccess = ref('')

let captchaTimer = null // 倒计时定时器

// ---- 表单校验 ----
// 手机号校验规则
const validatePhone = (phone) => {
  if (!phone) return '请输入手机号'
  if (!/^1[3-9]\d{9}$/.test(phone)) return '手机号格式不正确'
  return ''
}

// 验证码校验

// 实时校验（blur触发）
const validatePhoneOnBlur = () => {
  phoneError.value = validatePhone(bindForm.phone)
}

// 表单整体有效性（用于禁用按钮）
const isFormValid = computed(() => {
  return !validatePhone(bindForm.phone) && !validateCaptcha(bindForm.captcha)
})

// ---- 清除错误 ----
const clearPhoneError = () => {
  phoneError.value = ''
}
const clearCaptchaError = () => {
  captchaError.value = ''
  captchaSuccess.value = ''
}

// ---- 发送验证码（防抖+状态） ----
const sendCaptcha = async () => {
  // 防止重复点击
  if (isSendingCaptcha.value || captchaCooldown.value > 0) return

  clearPhoneError()
  clearCaptchaError()

  // 校验手机号
  const phoneErr = validatePhone(bindForm.phone)
  if (phoneErr) {
    phoneError.value = phoneErr
    return
  }

  isSendingCaptcha.value = true
  try {
    const response = await request.get('/auth/verify-code/phone', {
      params: {
        phone: bindForm.phone,
        scene: 'Bind'
      }
    })

    if (response.code === '200') {
      captchaSuccess.value = '验证码已发送'
      // 开始倒计时
      captchaCooldown.value = 60
      if (captchaTimer) clearInterval(captchaTimer)
      captchaTimer = setInterval(() => {
        captchaCooldown.value--
        if (captchaCooldown.value <= 0) {
          clearInterval(captchaTimer)
          captchaTimer = null
        }
      }, 1000)
    } else {
      ElMessage.error(response.message || '验证码发送失败')
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || '验证码发送失败，请重试'
    ElMessage.error(errorMsg)
    // 如果是手机号未注册等，可能需要在手机号上提示
    if (error.response?.data?.message?.includes('手机')) {
      phoneError.value = error.response.data.message
    }
  } finally {
    isSendingCaptcha.value = false
  }
}

// ---- 绑定处理（防重复提交） ----
const handleBind = async () => {
  // 防重复提交
  if (loading.value) return

  // 清除旧错误
  clearPhoneError()
  clearCaptchaError()

  // 全量校验
  const phoneErr = validatePhone(bindForm.phone)
  if (phoneErr) {
    phoneError.value = phoneErr
    return
  }
  const captchaErr = validateCaptcha(bindForm.captcha)
  if (captchaErr) {
    captchaError.value = captchaErr
    return
  }

  // sessionId 校验
  if (!props.sessionId) {
    ElMessage.error('会话已失效，请重新扫码')
    return
  }

  loading.value = true
  try {
    const response = await request.post('/auth/bind-phone/wechat', {
      phone: bindForm.phone,
      verifyCode: bindForm.captcha,
      sessionId: props.sessionId
    })

    if (response.code === '200') {
      // 保存token
setToken( response.data.token)

      // 成功反馈（由父组件处理关闭，但增加成功提示）
      ElMessage.success('手机绑定成功')
      emit('bind-success')
      dialogVisible.value = false
    } else {
      captchaError.value = response.message || '绑定失败，请重试'
    }
  } catch (error) {
    const msg = error.response?.data?.message
    if (msg) {
      if (msg.includes('手机')) {
        phoneError.value = msg
      } else if (msg.includes('验证码')) {
        captchaError.value = msg
      } else {
        ElMessage.error(msg)
      }
    } else {
      ElMessage.error('绑定失败，请检查网络后重试')
    }
  } finally {
    loading.value = false
  }
}

// ---- 组件生命周期 ----
// 监听 modelValue 变化
watch(() => props.modelValue, (newVal) => {
  dialogVisible.value = newVal
})

// 监听 dialogVisible 变化同步父组件
watch(dialogVisible, (newVal) => {
  emit('update:modelValue', newVal)
})

// 永真
// watch(() => props.modelValue, (newVal) => {
//   dialogVisible.value = newVal
// }, { immediate: true })

// 组件卸载时清除定时器
onBeforeUnmount(() => {
  if (captchaTimer) {
    clearInterval(captchaTimer)
    captchaTimer = null
  }
})
</script>

<style>
/* ========== 弹窗容器 ========== */
.bind-phone-dialog .el-dialog {
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(2px);
  background: rgba(255, 255, 255, 0.98);
}

.bind-phone-dialog .el-dialog__header {
  padding: 24px 28px 16px;
  margin: 0;
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;
}

.bind-phone-dialog .dialog-custom-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5px;
}

.bind-phone-dialog .dialog-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.bind-phone-dialog .el-dialog__body {
  padding: 28px;
}

.bind-phone-dialog .el-dialog__footer {
  padding: 0 28px 28px;
}

/* ========== 表单间距 ========== */
.bind-phone-dialog .el-form-item {
  margin-bottom: 18px;
}
.bind-phone-dialog .el-form-item:last-of-type {
  margin-bottom: 0;
}

/* ========== 输入框 ========== */
.bind-phone-dialog .el-input__wrapper {
  border-radius: 16px;
  box-shadow: 0 0 0 1px #e2e8f0 inset;
  height: 46px;
  padding: 0 18px;
  background: #ffffff;
  transition: box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1),
  background 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.bind-phone-dialog .el-input__wrapper:hover {
  box-shadow: 0 0 0 1px #94a3b8 inset;
  background: #f7f9fc;
}

.bind-phone-dialog .el-input__wrapper.is-focus {
  box-shadow: 0 0 0 2px #1f43cc inset, 0 0 0 4px rgba(31, 67, 204, 0.10);
  background: #ffffff;
}

.bind-phone-dialog .el-input__wrapper.is-error {
  box-shadow: 0 0 0 1px #dc2626 inset;
}
.bind-phone-dialog .el-input__wrapper.is-error.is-focus {
  box-shadow: 0 0 0 2px #dc2626 inset, 0 0 0 4px rgba(220, 38, 38, 0.10);
}

.bind-phone-dialog .el-input__inner {
  font-size: 15px;
  color: #0f172a;
  font-weight: 500;
  line-height: 28px;
}
.bind-phone-dialog .el-input__inner::placeholder {
  color: #94a3b8;
  font-weight: 400;
}

.bind-phone-dialog .el-input__prefix {
  display: flex;
  align-items: center;
  margin-right: 12px;
}
.bind-phone-dialog .el-input__prefix .el-icon {
  font-size: 22px;
  color: #94a3b8;
  transition: color 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.bind-phone-dialog .el-input__wrapper.is-focus .el-icon {
  color: #1f43cc;
}

.bind-phone-dialog .el-input__suffix {
  display: flex;
  align-items: center;
  padding-right: 4px;
}

/* ========== 验证码按钮 ========== */
.bind-phone-dialog .captcha-btn {
  font-weight: 600;
  color: #1f43cc;
  padding: 6px 14px;
  border-radius: 10px;
  background: transparent;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
}
.bind-phone-dialog .captcha-btn:hover:not(:disabled) {
  background: rgba(31, 67, 204, 0.08);
  color: #1836a8;
}
.bind-phone-dialog .captcha-btn:disabled {
  color: #94a3b8;
  background: transparent;
  cursor: not-allowed;
}

/* ========== 错误 / 成功提示 ========== */
.bind-phone-dialog .error-message,
.bind-phone-dialog .success-message {
  display: flex;
  align-items: center;
  font-size: 11px;
  margin-top: 6px;
  min-height: 22px;
  line-height: 1.5;
}
.bind-phone-dialog .error-message .el-icon,
.bind-phone-dialog .success-message .el-icon {
  margin-right: 6px;
  font-size: 13px;
}
.bind-phone-dialog .error-message {
  color: #dc2626;
}
.bind-phone-dialog .success-message {
  color: #16a34a;
}

/* ========== 微信绑定提示 ========== */
.bind-phone-dialog .wechat-bind-tip {
  margin-top: 20px;
  padding: 16px 18px;
  background: #f7f9fc;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  font-size: 0.85rem;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.bind-phone-dialog .wechat-bind-tip::before {
  content: '';
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #1f43cc;
  flex-shrink: 0;
}
.bind-phone-dialog .wechat-bind-tip p {
  margin: 0;
}

/* ========== 主按钮 ========== */
.bind-phone-dialog .el-button--primary {
  height: 46px;
  background: #1f43cc;
  border: none;
  border-radius: 16px;
  font-weight: 600;
  font-size: 1rem;
  letter-spacing: 0.01em;
  box-shadow: 0 4px 12px rgba(31, 67, 204, 0.30);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.bind-phone-dialog .el-button--primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(31, 67, 204, 0.35);
  background: #1836a8;
}
.bind-phone-dialog .el-button--primary:active {
  transform: translateY(0);
  box-shadow: 0 4px 12px rgba(31, 67, 204, 0.30);
}
.bind-phone-dialog .el-button--primary.is-loading {
  opacity: 0.85;
  transform: none !important;
}
</style>
